import { Button, Form, FormGroup, Modal, Radio, RadioGroup } from '@ksyos/design-system';
import { Component } from 'react';
import { getValidationErrorTranslation } from '@frontend/utils';
import type { ValidationErrors } from '../../../utils/validation';
import { YesOrNo } from '../../../../common/enums/yesOrNo';
import { YesOrNoOrSome } from '../../../../common/enums/yesOrNoOrSome';
import { evaluationDiagnosticReportSchema } from '../../../../common/validation/evaluation';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { EpisodeContext } from '../episodes/EpisodeContext';

interface IEvaluationDiagnosticReportProps {
    isOpen: boolean;
    onClose: () => Promise<void>;
    requestId: string;
}

interface IEvaluationDiagnosticReportState {
    errors: ValidationErrors;
    evaluation: IEvaluation;
    isSaving: boolean;
}

interface IEvaluation {
    afterwardsReferPatient: YesOrNo | null;
    reportHelpful: YesOrNoOrSome | null;
    reportInstructive: YesOrNoOrSome | null;
}

export class EvaluationDiagnosticReport extends Component<
    IEvaluationDiagnosticReportProps,
    IEvaluationDiagnosticReportState
> {
    constructor(props: IEvaluationDiagnosticReportProps, context?: {}) {
        super(props, context);
        this.state = {
            isSaving: false,
            errors: emptyValidationErrors,
            evaluation: {
                afterwardsReferPatient: null,
                reportHelpful: null,
                reportInstructive: null,
            },
        };
    }

    public render() {
        return (
            <EpisodeContext.Consumer>
                {(context) => (
                    <Modal
                        closeOnEsc={false}
                        isOpen={this.props.isOpen}
                        onClose={this.props.onClose}
                        size="large"
                    >
                        <Modal.Header>{translateKey('evaluation.question')}</Modal.Header>
                        <Modal.Body className="automation-modal-body">
                            <Form submitOnEnter={false}>
                                <FormGroup
                                    label={translateKey('evaluation.afterwardsReferPatient')}
                                    layout="vertical"
                                >
                                    <RadioGroup
                                        error={getValidationErrorTranslation(
                                            this.state.errors.get('afterwardsReferPatient')
                                        )}
                                        layout="row"
                                        name="afterwardsReferPatient"
                                        onChange={async (event) =>
                                            await this.handleChange(
                                                'afterwardsReferPatient',
                                                event.target.value as YesOrNo
                                            )
                                        }
                                        value={this.state.evaluation.afterwardsReferPatient ?? undefined}
                                    >
                                        <Radio
                                            key="radio1"
                                            label={translateKey('common.no')}
                                            value={YesOrNo.no}
                                        />
                                        <Radio
                                            key="radio2"
                                            label={translateKey('common.yes')}
                                            value={YesOrNo.yes}
                                        />
                                    </RadioGroup>
                                </FormGroup>
                                <FormGroup
                                    label={translateKey('evaluation.reportHelpful')}
                                    layout="vertical"
                                >
                                    <RadioGroup
                                        error={getValidationErrorTranslation(this.state.errors.get('reportHelpful'))}
                                        layout="row"
                                        name="reportHelpful"
                                        onChange={async (event) =>
                                            await this.handleChange(
                                                'reportHelpful',
                                                event.target.value as YesOrNoOrSome
                                            )
                                        }
                                        value={this.state.evaluation.reportHelpful ?? undefined}
                                    >
                                        <Radio
                                            key="radio1"
                                            label={translateKey('common.no')}
                                            value={YesOrNo.no}
                                        />
                                        <Radio
                                            key="radio2"
                                            label={translateKey('common.some')}
                                            value={YesOrNoOrSome.some}
                                        />
                                        <Radio
                                            key="radio3"
                                            label={translateKey('common.yes')}
                                            value={YesOrNo.yes}
                                        />
                                    </RadioGroup>
                                </FormGroup>
                                <FormGroup
                                    label={translateKey('evaluation.reportInstructive')}
                                    layout="vertical"
                                >
                                    <RadioGroup
                                        error={getValidationErrorTranslation(
                                            this.state.errors.get('reportInstructive')
                                        )}
                                        layout="row"
                                        name="reportInstructive"
                                        onChange={async (event) =>
                                            await this.handleChange(
                                                'reportInstructive',
                                                event.target.value as YesOrNoOrSome
                                            )
                                        }
                                        value={this.state.evaluation.reportInstructive ?? undefined}
                                    >
                                        <Radio
                                            key="radio1"
                                            label={translateKey('common.no')}
                                            value={YesOrNo.no}
                                        />
                                        <Radio
                                            key="radio2"
                                            label={translateKey('common.some')}
                                            value={YesOrNoOrSome.some}
                                        />
                                        <Radio
                                            key="radio3"
                                            label={translateKey('common.yes')}
                                            value={YesOrNo.yes}
                                        />
                                    </RadioGroup>
                                </FormGroup>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer className="automation-modal-footer">
                            <Button
                                className="automation-btn-saveAndProceed"
                                isDisabled={this.state.isSaving}
                                onClick={async () => {
                                    await this.save();
                                    context?.changeShowRecommendations(true);
                                }}
                            >
                                {translateKey('common.saveAndProceed')}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}
            </EpisodeContext.Consumer>
        );
    }

    private async handleChange<K extends keyof IEvaluation>(name: K, value: IEvaluation[K]) {
        await this.setState(({ evaluation }) => ({ evaluation: { ...evaluation, [name]: value } }));
    }

    private async save() {
        const url = '/api/evaluations/saveEvaluationDiagnosticReport';

        const { errors, item } = validate(evaluationDiagnosticReportSchema, {
            ...this.state.evaluation,
            requestId: this.props.requestId,
        });
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }
        await this.setState({ isSaving: true });
        await fetchJson(url, 'POST', item);

        await this.props.onClose();
    }
}
